import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Col, Row, Label, FormGroup, Card, CardBody } from "reactstrap";
import { CKEditor } from "ckeditor4-react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";
import ConfigurationService from "./configuration.service";
import AuthService from "../Authentication/auth.service";
import PermissionAccess from '../../PermissionAccess/Access.js';

const TabSupportInfo = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);

    const [pharmacySupport, setPharmacySupport] = useState("");
    const [doctorSupport, setDoctorSupport] = useState("");
    const [patientSupport, setPatientSupport] = useState("");

    const { authData } = PermissionAccess();

    useEffect(() => {
        i18n.changeLanguage(localStorage.getItem('language'));
        getSystemConfig();
    }, []);

    function getSystemConfig() {
        setLoading(true);
        ConfigurationService.getConfiguration(authData.sessionID).then((response) => {
            setLoading(false);
            if (response.data.status) {
                const rec_data = response.data.data[0];
                setConfigs(rec_data);
                setPharmacySupport(rec_data.support_info_pharmacy || "");
                setDoctorSupport(rec_data.support_info_doctor || "");
                setPatientSupport(rec_data.support_info_patient || "");
                setDataLoaded(true);
            } else {
                if (response.data.message === "Unauthorized") {
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                } else {
                    Alerts.swalErrorAlert(t("Unable to retreive configurations at the moment."));
                }
            }
        }, (error) => {
            setLoading(false);
            Alerts.swalErrorAlert(t("Unable to retreive configurations at the moment."));
        });
    }

    const onSubmit = () => {
        const payloadData = {
            functionName: "SetConfigurations",
            postData: {
                ...configs,
                support_info_pharmacy: pharmacySupport,
                support_info_doctor: doctorSupport,
                support_info_patient: patientSupport
            }
        };

        setLoading(true);
        ConfigurationService.setConfiguration(authData.sessionID, payloadData).then((response) => {
            setLoading(false);
            if (response.data.status) {
                Alerts.swalSuccessAlert(t("Support Information updated successfully."));
            } else {
                Alerts.swalErrorAlert(response.data.message || t("Failed to update configurations."));
            }
        }, (error) => {
            setLoading(false);
            Alerts.swalErrorAlert(t("Network error. Please try again."));
        });
    };

    return (
        <Fragment>
            <div className="container-fluid">
                <Row>
                    <Col md={12}>
                        <Card className="main-card mb-3 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h4 className="fw-bold text-dark mb-1">{t('Support Information Settings')}</h4>
                                        <p className="text-muted mb-0">{t('Manage and update your support information for all apps.')}</p>
                                    </div>
                                </div>

                                <LoadingOverlay tag="div" active={loading}
                                    styles={{ overlay: (base) => ({ ...base, borderRadius: '15px' }) }}
                                    spinner={<DNALoader />}>

                                    <Row>
                                        {/* Pharmacy App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-headset me-2 text-primary"></i>
                                                        {t('Pharmacy App Support Info')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={pharmacySupport}
                                                                onChange={(evt) => setPharmacySupport(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Doctor App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-stethoscope me-2 text-info"></i>
                                                        {t('Doctor App Support Info')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={doctorSupport}
                                                                onChange={(evt) => setDoctorSupport(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Patient App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-hands-helping me-2 text-success"></i>
                                                        {t('Patient App Support Info')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={patientSupport}
                                                                onChange={(evt) => setPatientSupport(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Action Button */}
                                    <div className="d-flex justify-content-center mt-4">
                                        <button
                                            className="btn btn-lg px-5 py-3"
                                            type="button"
                                            onClick={onSubmit}
                                            style={{
                                                backgroundColor: 'var(--buttons-color)',
                                                borderColor: 'var(--buttons-color)',
                                                color: 'var(--buttons-text-color)',
                                                borderRadius: '15px',
                                                fontWeight: '600',
                                                minWidth: '200px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}
                                        >
                                            <i className="fas fa-save me-2"></i>
                                            {t('Save Support Info')}
                                        </button>
                                    </div>
                                </LoadingOverlay>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

export default TabSupportInfo;
